import {
  NoCompletedConversations,
  NoPendingRequests,
  NoReferrals,
} from '../../assets'
import { SelectedCard } from '../../pages/supporter-flow/supporter-interfaces'
import {
  IDayTypeInterface,
  ISlotTypeInterface,
} from '../../pages/home/middleware/metadata-middleware'
import moment from 'moment'
import { Roles } from '../../pages/constants'
import {
  IMonitorRequestData,
  MonitorRequestColumnProps,
} from '../../pages/admin-flow/admin-flow-interfaces'

/**
 * Helper function to get the appropriate email for a user.
 * For external users (with #EXT# in UPN), prefer mail over userPrincipalName.
 * For internal users, prefer userPrincipalName for consistency with SSO.
 *
 * @param userPrincipalName - User Principal Name from Azure AD
 * @param mail - Mail property from Azure AD
 * @returns The appropriate email address to use
 */
export const getUserEmail = (
  userPrincipalName: string | null | undefined,
  mail: string | null | undefined
): string => {
  // If UPN contains #EXT# (external/guest user), prefer the mail field
  // because external UPN format is not a real email address
  if (userPrincipalName && userPrincipalName.includes('#EXT#')) {
    return mail || userPrincipalName
  }
  // For internal users, prefer UPN for consistency with SSO
  return userPrincipalName ?? mail ?? ''
}

export const getTableDetails = (selectedCard: SelectedCard) => {
  const cardDetails = {
    'pending-conversations': {
      tableTitle: 'Pending Conversations',
      image: NoPendingRequests,
      emptyMessage: 'There are no pending tasks or requests to address.',
    },
    'awaiting-referrals': {
      tableTitle: 'Referrals Awaiting Match',
      image: NoReferrals,
      emptyMessage:
        "Unfortunately, we couldn't locate any data for your request.",
    },
    'completed-conversations': {
      tableTitle: 'Completed Conversations',
      image: NoCompletedConversations,
      emptyMessage:
        'There are no completed conversations to display at the moment.',
    },
  }

  return cardDetails[selectedCard]
}

export function isValidAction(value: string): value is SelectedCard {
  return Object.values(SelectedCard).includes(value as SelectedCard)
}

export interface IAddSupporterRequest {
  emailId: string
  secondaryEmailId: string
  name: string
  jobRole: string
  contactPreference: string
  phoneNumber: string
  languages: string
  gender: string
  location: string
  status: string
  isTrained: boolean
  otherRoleType?: string
}

export interface IUpdateSupporterRequest extends IAddSupporterRequest {
  conversationsCompleted: number
}

export interface IAddAdmin {
  emailId: string
  name: string
}

export interface IPeerRequest {
  referredPeerOid: string
  typeOfRequest: string
  referredPeerName: string
  referredPeerLocation: string
  referredPeerEmailId: string
  referredPeerRoleType: string
  otherRoleType?: string
  language: string
  gender: string
  location: string
  meetingPreference: string
  phoneNumber?: string
  reason: string
  additionalInfo: string
  addedReason?: string
  meetingSlots: IMeetingSlot[]
}

export interface INominationRequest {
  emailId: string
  secondaryEmailId: string
  name: string
  jobRole: string
  contactPreference: string
  phoneNumber: string
  languages: string
  gender: string
  location: string
  referredBy: string
  referredByEmailId: string
  typeOfRequest: string
}

export interface IEditPeerRequest {
  id: string
  typeOfRequest: string
  referredPeerName: string
  referredPeerLocation: string
  referredPeerEmailId: string
  referredPeerRoleType: string
  otherRoleType?: string
  language: string
  gender: string
  location: string
  meetingPreference: string
  phoneNumber?: string
  reason: string
  additionalInfo: string
  addedReason?: string
  meetingSlots: IMeetingSlot[]
}

export interface IGetRequestByType {
  page?: number
  size?: number
  filters?: Ifilters[]
  type: string
}

interface IMeetingSlot {
  dayType: string
  slotType: string
}

interface Ifilters {
  filterColumn: string
  filterValues: string[]
}

export const getMeetingTypePayload = (
  selectedSlots: { [key: number]: boolean },
  dayTypes: IDayTypeInterface[],
  slotTypes: ISlotTypeInterface[]
): IMeetingSlot[] => {
  const slotCodes = ['Morning', 'Afternoon', 'Evening']

  return Object.entries(selectedSlots).reduce(
    (acc: IMeetingSlot[], [keyStr, isSelected]) => {
      const key = Number(keyStr)

      if (isSelected) {
        let dayTypeCode: string
        if (key < 3) {
          dayTypeCode = 'Weekday'
        } else if (key < 6) {
          dayTypeCode = 'Weekend'
        } else {
          dayTypeCode = 'Variable'
        }

        const slotTypeCode = slotCodes[key % 3]

        const dayTypeId = dayTypes.find((day) => day.code === dayTypeCode)?.id
        const slotTypeId = slotTypes.find(
          (slot) => slot.code === slotTypeCode
        )?.id

        if (dayTypeId && slotTypeId) {
          acc.push({ dayType: dayTypeId, slotType: slotTypeId })
        }
      }

      return acc
    },
    []
  )
}
export const getTextForWeekdaysAndWeekends = (preference: string) => {
  const currentPreference = preference.toLocaleLowerCase()

  const hasWeekday = currentPreference.includes('weekday')
  const hasWeekend = currentPreference.includes('weekend')
  const hasVariable = currentPreference.includes('variable')

  const parts: string[] = []

  if (hasWeekday) {
    parts.push('weekdays')
  }

  if (hasWeekend) {
    parts.push('weekends')
  }

  if (hasVariable) {
    parts.push('variable schedule')
  }

  if (parts.length === 0) {
    return 'no preference'
  } else if (parts.length === 1) {
    return parts[0]
  } else if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`
  } else {
    return `${parts[0]}, ${parts[1]}, and ${parts[2]}`
  }
}

export const getInitialSelectedSlots = (scheduleLength = 3) => {
  return Array(scheduleLength * 3).fill(false)
}

export const getDayTypeFromIndex = (index: number) => {
  if (index < 3) return 'Weekday'
  if (index < 6) return 'Weekend'
  return 'Variable'
}

export const getSlotTypeFromIndex = (index: number) => {
  const slotTypes = ['Morning', 'Afternoon', 'Evening']
  return slotTypes[index % 3]
}

export const fetchRequestStatusId = (action: string) => {
  let name = ''
  if (action === 'awaiting-referrals') {
    name = 'Awaiting'
  } else if (action === 'pending-conversations') {
    name = 'Pending'
  } else if (action === 'completed-conversations') {
    name = 'Completed'
  }
  return name
}

export const getLastUpdatedAt = (createdAt: string) => {
  const createdAtMoment = moment.parseZone(createdAt)
  const nowMoment = moment.parseZone()

  const diffInDays = nowMoment.diff(createdAtMoment, 'days')
  const diffInHours = nowMoment.diff(
    createdAtMoment.add(diffInDays, 'days'),
    'hours'
  )
  const diffInMinutes = nowMoment.diff(
    createdAtMoment.add(diffInDays, 'days').add(diffInHours, 'hours'),
    'minutes'
  )

  return `last updated ${
    diffInDays > 0 ? diffInDays + ' day' + (diffInDays > 1 ? 's' : '') : ''
  } ${diffInHours > 0 ? diffInHours + ' hours ' : ''} ${
    diffInMinutes > 0 ? diffInMinutes + ' minutes ' : ''
  } ${
    diffInDays === 0 && diffInHours === 0 && diffInMinutes === 0
      ? 'just a few moments'
      : ''
  } ago.`
}

export const getUserRole = (
  roles: string[],
  isPeerSupporter: boolean,
  isPeerSupporterLead: boolean
) => {
  if (roles.includes('P2PAdmin') && isPeerSupporter && isPeerSupporterLead)
    return Roles.ALL
  if (roles.includes('P2PAdmin') && isPeerSupporterLead) return Roles.ADMIN_LEAD
  if (roles.includes('P2PAdmin') && isPeerSupporter)
    return Roles.ADMIN_SUPPORTER
  if (isPeerSupporterLead && isPeerSupporter) return Roles.LEAD_SUPPORTER
  if (roles.includes('P2PAdmin')) return Roles.ADMIN
  if (isPeerSupporterLead) return Roles.PEER_SUPPORTER_LEAD
  if (isPeerSupporter) return Roles.SUPPORTER
  return Roles.USER
}

export const convertArrayToMonitorRequestColumnProps = (
  dataArray: IMonitorRequestData[]
): MonitorRequestColumnProps[] => {
  return dataArray.map((data) => ({
    phoneNumber: data.phoneNumber,
    emailId: data.referredPeerEmailId,
    meetingSlots: data.meetingSlots,
    referredPeerLocation: data.referredPeerLocation.name,
    reason: data.reason.name,
    additionalInfo: data.additionalInfo,
    addedReason: data.addedReason,
    supporterEmail: data.peerSupporterEmail || 'Unassigned',
    requestId: data.id,
    id: parseInt(data.id) || 0,
    name: data.referredPeerName,
    firstName: data.referredPeerName?.split(' ')[0] || '',
    lastName: data.referredPeerName?.split(' ').slice(1).join(' ') || '',
    language: data.language.name,
    location: data.location.name,
    gender: data.gender.name,
    roleType: data.referredPeerRoleType.name,
    meetingSlot:
      data.meetingSlots.length > 0
        ? data.meetingSlots.map((slot) => slot.slotType.name).join(', ')
        : '',
    requestType: data.requestType.name,
    dateTime: formatTimestamp(data.statusLastUpdatedAt),
    conversationStatus: data.status.name,
    supporter: data.peerSupporterName || 'Unassigned',
    availabilityPreferences: data.meetingPreference.name,
    matchedPeerSupporters: data.matchedPeerSupporters || [],
    state: data.state?.name || '',
    genderId: undefined,
    locationId: data.referredPeerLocation.id,
    stateId: data.state?.id,
    languageIds: [],
    roleTypeId: data.referredPeerRoleType.id,
    reasonForReferral: data.reason.name,
    reasonForReferralId: data.reason.id,
    requestTypeId: data.requestType.id,
    meetingPreferenceId: data.meetingPreference.id,
    referredPeerLocationId: data.referredPeerLocation.id,
    supporterGenderId: data.gender.id,
    supporterLanguageIds: [data.language.id],
    supporterLocationId: data.location.id,
    supporterRoleTypeId: undefined,
  }))
}

export const formatTimestamp = (timestamp: string): string => {
  const parsedDate = moment.parseZone(timestamp)
  // Format it to 'MM/DD/YYYY HH:mm A'
  return parsedDate.format('MM/DD/YYYY HH:mm A')
}

export const getArticle = (word: string) => {
  if (!word) return 'A' // Default to 'A' if the word is empty or undefined

  const firstChar = word.charAt(0).toLowerCase()
  const vowels = ['a', 'e', 'i', 'o', 'u']

  return vowels.includes(firstChar) ? 'An' : 'A'
}
